/* ============================================================
   EDITORIAL — JS compartido para la biblioteca de blog.
   Idempotente y compatible con el editor de tema de Shopify.
   Auto-inicia por data-attributes. No depende de librerías.
   ============================================================ */
(function () {
  'use strict';

  // El snippet se incluye por sección, así que este archivo puede cargarse varias veces.
  // Garantizamos que toda la lógica (lightbox, listeners) se inicialice UNA sola vez.
  if (window.__editorialJsInit) return;
  window.__editorialJsInit = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reading progress bar ---------- */
  function initProgress() {
    var bars = document.querySelectorAll('.editorial .progress:not([data-init])');
    if (!bars.length) return;
    bars.forEach(function (bar) { bar.setAttribute('data-init', ''); });
    if (initProgress.bound) return;
    initProgress.bound = true;
    var update = function () {
      var h = document.documentElement;
      var max = h.scrollHeight - h.clientHeight;
      var s = max > 0 ? h.scrollTop / max : 0;
      document.querySelectorAll('.editorial .progress').forEach(function (b) {
        b.style.width = (s * 100) + '%';
      });
    };
    window.addEventListener('scroll', function () { window.requestAnimationFrame(update); }, { passive: true });
    update();
  }

  /* ---------- Scroll reveal ---------- */
  function initReveal() {
    var els = document.querySelectorAll('.editorial [data-reveal]:not([data-reveal-init])');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.setAttribute('data-reveal-init', ''); el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    els.forEach(function (el) { el.setAttribute('data-reveal-init', ''); io.observe(el); });
  }

  /* ---------- Count-up stats ---------- */
  function initStats() {
    var nums = document.querySelectorAll('.editorial .stat .num[data-count]:not([data-count-init])');
    if (!nums.length) return;
    nums.forEach(function (el) {
      el.setAttribute('data-count-init', '');
      var target = parseFloat(el.getAttribute('data-count')) || 0;
      var decimals = parseInt(el.getAttribute('data-decimals') || '0', 10);
      var prefix = el.getAttribute('data-prefix') || '';
      var suffix = el.getAttribute('data-suffix') || '';
      var render = function (v) { el.textContent = prefix + v.toFixed(decimals) + suffix; };
      if (reduceMotion || !('IntersectionObserver' in window)) { render(target); return; }
      render(0);
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.unobserve(e.target);
          var dur = 1400, start = null;
          var step = function (ts) {
            if (!start) start = ts;
            var p = Math.min((ts - start) / dur, 1);
            var eased = 1 - Math.pow(1 - p, 3);
            render(target * eased);
            if (p < 1) window.requestAnimationFrame(step);
          };
          window.requestAnimationFrame(step);
        });
      }, { threshold: 0.4 });
      io.observe(el);
    });
  }

  /* ---------- Before / After slider ---------- */
  function initBeforeAfter() {
    var ranges = document.querySelectorAll('.editorial .ed-ba__range:not([data-init])');
    ranges.forEach(function (r) {
      r.setAttribute('data-init', '');
      var frame = r.closest('.ed-ba__frame');
      var sync = function () { if (frame) frame.style.setProperty('--pos', r.value + '%'); };
      r.addEventListener('input', sync);
      sync();
    });
  }

  /* ---------- TOC active link sync ---------- */
  function initToc() {
    var tocs = document.querySelectorAll('.editorial .ed-toc:not([data-init])');
    tocs.forEach(function (toc) {
      toc.setAttribute('data-init', '');
      var links = Array.prototype.slice.call(toc.querySelectorAll('a[href^="#"]'));
      var targets = links.map(function (a) { return document.getElementById(a.getAttribute('href').slice(1)); });
      var onScroll = function () {
        var active = -1;
        targets.forEach(function (t, i) {
          if (t && t.getBoundingClientRect().top <= window.innerHeight * 0.4) active = i;
        });
        links.forEach(function (a, i) { a.classList.toggle('active', i === active); });
      };
      window.addEventListener('scroll', function () { window.requestAnimationFrame(onScroll); }, { passive: true });
      onScroll();
    });
  }

  /* ---------- Smooth scroll for in-page anchors ---------- */
  function initSmoothScroll() {
    if (initSmoothScroll.bound) return;
    initSmoothScroll.bound = true;
    document.addEventListener('click', function (e) {
      var a = e.target.closest('.editorial a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href').slice(1);
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      var header = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 80;
      window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - header - 12, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  }

  /* ============================================================
     LIGHTBOX con zoom / rotación / pan / navegación
     Cualquier [data-lightbox] (con data-full = url grande) lo abre.
     Las imágenes con el mismo data-gallery comparten navegación.
     ============================================================ */
  var LB = null;
  function buildLightbox() {
    if (LB) return LB;
    var el = document.createElement('div');
    el.className = 'ed-lightbox';
    el.innerHTML =
      '<button class="ed-lightbox__close" data-act="close" aria-label="Cerrar">&#10005;</button>' +
      '<button class="ed-lightbox__nav prev" data-act="prev" aria-label="Anterior">&#8249;</button>' +
      '<button class="ed-lightbox__nav next" data-act="next" aria-label="Siguiente">&#8250;</button>' +
      '<figure class="ed-lightbox__fig"><img class="ed-lightbox__img" alt=""><figcaption class="ed-lightbox__cap"></figcaption></figure>';
    document.body.appendChild(el);

    var img = el.querySelector('.ed-lightbox__img');
    var cap = el.querySelector('.ed-lightbox__cap');
    var prevBtn = el.querySelector('.prev');
    var nextBtn = el.querySelector('.next');
    var state = { list: [], idx: 0 };

    function load(i) {
      if (!state.list.length) return;
      state.idx = (i + state.list.length) % state.list.length;
      var item = state.list[state.idx];
      // Ocultar hasta que la NUEVA imagen cargue (evita ver la carta anterior).
      img.style.opacity = '0';
      img.onload = function () { img.style.opacity = '1'; };
      img.src = item.full;
      if (img.complete) { img.style.opacity = '1'; }
      img.alt = item.alt || '';
      cap.textContent = item.cap || '';
      cap.style.display = item.cap ? '' : 'none';
      prevBtn.style.display = nextBtn.style.display = state.list.length > 1 ? '' : 'none';
    }
    LB = {
      open: function (list, idx) { state.list = list; el.classList.add('open'); document.body.style.overflow = 'hidden'; load(idx); },
      close: function () { el.classList.remove('open'); document.body.style.overflow = ''; },
      next: function () { load(state.idx + 1); },
      prev: function () { load(state.idx - 1); }
    };

    el.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-act]');
      if (btn) {
        var act = btn.getAttribute('data-act');
        if (act === 'close') LB.close();
        else if (act === 'next') LB.next();
        else if (act === 'prev') LB.prev();
        return;
      }
      if (e.target !== img) LB.close();
    });

    document.addEventListener('keydown', function (e) {
      if (!el.classList.contains('open')) return;
      if (e.key === 'Escape') LB.close();
      else if (e.key === 'ArrowRight') LB.next();
      else if (e.key === 'ArrowLeft') LB.prev();
    });

    return LB;
  }

  function gatherGallery(trigger) {
    var group = trigger.getAttribute('data-gallery');
    var nodes = group
      ? document.querySelectorAll('.editorial [data-lightbox][data-gallery="' + group + '"]')
      : [trigger];
    var list = [], start = 0;
    Array.prototype.forEach.call(nodes, function (n, i) {
      if (n === trigger) start = i;
      list.push({ full: n.getAttribute('data-full') || (n.querySelector('img') && n.querySelector('img').src), alt: n.getAttribute('data-alt') || '', cap: n.getAttribute('data-cap') || '' });
    });
    return { list: list, start: start };
  }

  function initLightbox() {
    if (initLightbox.bound) return;
    initLightbox.bound = true;
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('.editorial [data-lightbox]');
      if (!trigger) return;
      if (trigger.tagName === 'A') e.preventDefault();
      var g = gatherGallery(trigger);
      if (!g.list.length || !g.list[g.start].full) return;
      buildLightbox().open(g.list, g.start);
    });
  }

  /* ---------- Boot ---------- */
  function boot() {
    initProgress();
    initReveal();
    initStats();
    initBeforeAfter();
    initToc();
    initSmoothScroll();
    initLightbox();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }

  // Theme editor compatibility
  document.addEventListener('shopify:section:load', boot);
})();
